import { useContext, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { MenuContext } from 'context/MenuContext';
import useScrollPosition from 'hooks/useScrollPosition';
import useScrollLock from 'hooks/useScrollLock.js';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
// import content from 'content/navigation';
import MobileMenuButton from './MobileMenuButton';
import PropTypes from 'prop-types';
import NewYorkTime from 'components/NewYorkTime';

const Container = styled.header`
  user-select: none;
  visibility: ${({ scrolled, revealOnScroll }) =>
    revealOnScroll ? (scrolled ? 'visible' : 'hidden') : 'visible'};
  opacity: ${({ scrolled, revealOnScroll }) =>
    revealOnScroll ? (scrolled ? '1' : '0') : '1'};
  width: 100vw;
  position: fixed;
  top: 0;
  z-index: 999;
  color: ${({ theme }) => theme.colors.white};

  transition:
    visibility 0.3s ease,
    opacity 0.3s ease;
`;

const Content = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  padding: 2rem;
  position: relative;

  ${({ theme }) => theme.mediaQueries.lg} {
    padding: 2rem 3rem;
  }
`;

const Logo = styled.h1`
  ${({ theme }) => theme.mixins.MonoM};
  transition: color 0.2s ease;

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const NavRight = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  gap: 2rem;
  ${({ theme }) => theme.mixins.MonoM};

  ${({ theme }) => theme.mediaQueries.lg} {
    flex-direction: row;
    gap: 8rem;
  }
`;

const Clock = styled.span`
  opacity: ${({ isAnimating, menuOpen }) =>
    menuOpen ? (isAnimating ? 0 : 1) : 1};
  animation: ${({ isAnimating }) => (isAnimating ? fadeIn : 'none')} 0.4s ease
    forwards;
  animation-delay: ${({ index }) => index * 0.1}s;
  p:first-of-type {
    color: ${({ theme }) => theme.colors.grayLight};
  }
  display: flex;
  gap: 4rem;

  ${({ theme }) => theme.mediaQueries.lg} {
    opacity: 1;
  }
`;

const Contact = styled.p`
  ${({ theme }) => theme.mixins.MonoM};
  transition: color 0.2s ease;

  ${({ theme }) => theme.mixins.MonoM};
  transition: color 0.2s ease;
  opacity: ${({ isAnimating, menuOpen }) =>
    menuOpen ? (isAnimating ? 0 : 1) : 1};
  animation: ${({ isAnimating }) => (isAnimating ? fadeIn : 'none')} 0.4s ease
    forwards;
  animation-delay: ${({ index }) => index * 0.1}s;

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      color: ${({ theme }) => theme.colors.accent};
      cursor: pointer;
    }
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    opacity: 1;
  }
`;

const NavLinks = styled.nav`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  align-items: flex-end;
  gap: 2rem;
  position: absolute;
  top: 0rem;
  left: 0;
  margin-top: 6rem;
  padding: 0 2rem;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.lg} {
    display: flex;
    flex-direction: row;
    position: relative;
    right: 0rem;
    margin-top: 0rem;
    padding: 0rem;
    width: max-content;
    height: max-content;
  }
`;
// const StyledNavLink = styled(NavLink)`
//   ${({ theme }) => theme.mixins.Mono};
// opacity: ${({ isAnimating, menuOpen }) =>
//   menuOpen ? (isAnimating ? 0 : 1) : 0};
// animation: ${({ isAnimating }) => (isAnimating ? fadeIn : 'none')} 0.4s ease
//   forwards;
// animation-delay: ${({ index }) => index * 0.1}s;
//   position: relative;
//   transition: color 0.3s ease;

//   &:after {
//     content: '';
//     height: 1.5px;
//     width: 100%;
//     background-color: ${({ theme }) => theme.colors.accentSecondary};
//     position: absolute;
//     bottom: 0;
//     left: 0;
//     transform-origin: left;
//     transform: scaleX(0);
//     transition: transform 0.3s ease;
//   }

//   &.active {
//     color: ${({ theme }) => theme.colors.accentSecondary};

//     &:after {
//       transform: scaleX(1);
//     }
//   }

//   ${({ theme }) => theme.mediaQueries.sm} {
//     &.active {
//       &:hover {
//         color: ${({ theme }) => theme.colors.white};

//         &:after {
//           transform-origin: right;
//           transform: scaleX(0);
//         }
//       }
//     }
//     &:hover {
//       color: ${({ theme }) => theme.colors.accentSecondary};

//       &:after {
//         transform: scaleX(1);
//       }
//     }
//   }

//   ${({ theme }) => theme.mediaQueries.sm} {
//     opacity: 1;
//   }
// `;
const MobileBackgound = styled.div`
  height: 100svh;
  width: 100vw;
  background-color: ${({ theme }) => theme.colors.black};
  position: fixed;
  top: 0;
  left: 0;
  z-index: 998;
  transform: scaleY(${({ menuOpen }) => (menuOpen ? '1' : '0')});
  transform-origin: top;
  transition: transform 0.6s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 1rem;

  ${({ theme }) => theme.mediaQueries.lg} {
    display: none;
  }
`;

const fadeIn = keyframes`
from {
  opacity: 0;
}
to {
  opacity: 1;
}
`;

const handleScroll = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Header = ({ revealOnScroll = false }) => {
  // const navLinks = content.navLinks;

  const scrolled = useScrollPosition(0);

  const { menuOpen, toggleMenu, menuJustOpened } = useContext(MenuContext);
  useScrollLock(menuOpen);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 640 && menuOpen) {
        toggleMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuOpen, toggleMenu]);

  const handleNavClick = () => {
    if (menuOpen) {
      toggleMenu();
    }
  };

  return (
    <>
      <Container scrolled={scrolled} revealOnScroll={revealOnScroll}>
        <Content>
          <Logo>
            <NavLink to="/" onClick={handleNavClick}>
              Tyron Shi
            </NavLink>
          </Logo>
          <NavLinks isOpen={menuOpen}>
            <NavRight>
              <Clock isAnimating={menuJustOpened}>
                <NewYorkTime />
                <p>Manhattan, New York</p>
              </Clock>
              <Contact
                onClick={() => {
                  handleScroll('contact');
                  handleNavClick();
                }}
                isAnimating={menuJustOpened}
              >
                Contact
              </Contact>
            </NavRight>
            {/* {navLinks.map((navLink, index) => (
              <StyledNavLink
                key={index}
                to={navLink.url}
                onClick={handleNavClick}
                menuOpen={menuOpen}
                isAnimating={menuJustOpened}
                index={index}
              >
                {navLink.label}
              </StyledNavLink>
            ))} */}
          </NavLinks>
          <MobileMenuButton onClick={toggleMenu} isOpen={menuOpen} />
        </Content>
      </Container>
      <MobileBackgound aria-hidden="true" menuOpen={menuOpen}></MobileBackgound>
    </>
  );
};
Header.propTypes = {
  revealOnScroll: PropTypes.oneOf(['true', 'false']),
};
export default Header;
